import { CardType, NULLIFY_FIELD } from '../constants/payments';
import {
  IPaymentCardCommonData,
  IPaymentCardsCommonData,
  IPaymentsPagination,
  TPaymentFormValues,
} from '../models/common/payments';
import {
  IB2CBillingAddress,
  IB2CPaginationInfo,
  IB2CPaymentCard,
  TB2CEditPaymentRequestData,
} from '../models/b2c/payments';
import {
  IB2BBillingAddress,
  IB2BPaymentCard,
  IB2BPaymentsPagination,
  T2CMultipleApproverResponse,
} from '../models/b2b/payments';
import { IFilterConfig } from '../api/b2b/payments';

export const mapB2BPaymentCardsToCommonModel = ({
  paymentCards,
  paginationInfo,
  filterConfig,
  oldPaginationInfo,
  businessName,
  t2cNumber,
  t2cApprovers,
}: {
  paymentCards: Partial<IB2BPaymentCard>[];
  paginationInfo: Partial<IB2BPaymentsPagination>;
  filterConfig?: IFilterConfig;
  oldPaginationInfo?: IPaymentsPagination | null;
  businessName?: string;
  t2cNumber?: string;
  t2cApprovers: T2CMultipleApproverResponse | null;
}): IPaymentCardsCommonData => {
  const mappedCards = paymentCards.map((card): IPaymentCardCommonData => {
    return {
      addedBy: card.addedBy,
      actions: card.actions,
      operations: card.operations,
      addressId: card.billingAddress?.addressId || '',
      addressLine1: card.billingAddress?.addressLine1 || '',
      addressLine2: card.billingAddress?.addressLine2 || '',
      cardBrand: card.cardBrand || '',
      cardExpiryMonth: card.cardExpiryMonth || '',
      cardExpiryYear: card.cardExpiryYear || '',
      cardHolderName: card.cardHolderName || '',
      cardNickName: card.cardNickName,
      cardNumber: card.cardNumber || '',
      cardNumberLast4: card.cardNumberLast4 || '',
      cardStatus: card.cardStatus,
      cardType: card.cardBrandType || '',
      city: card.billingAddress?.cityName || '',
      authorizeFlag: card.authorizeFlag,
      isCardExpired: card.isCardExpired,
      isDefault: card.isDefault || false,
      lastModifiedDate: card.lastModifiedDate,
      paymentId: card.paymentId || '',
      state: card.billingAddress?.stateCode || '',
      zipCode: card.billingAddress?.postalCode || '',
      buyerId: card.buyerId || '',
      t2cStatus: card.t2cStatus,
      t2cNumber: card.t2cNumber || undefined,
      activeT2COnFile:
        paginationInfo.t2cNumber || card.t2cNumber || t2cNumber || null,
      t2cPrimary: card.t2cPrimary,
      paypalAccountId: card.paypalAccountId,
      rewardExpDate: card.rewardExpDate,
      rewardStartDate: card.rewardStartDate,
      gcBalance: card.gcBalance,
      userId: card.userId,
      level: card.level || '',
      citiAccountId: card.citiAccountId || '',
      citiPrimary: card.citiPrimary || '',
      trackingOnly: card.trackingOnly,
      hdWalletAuthorized: card.hdWalletAuthorized || 'N',
      locType: card.locType,
      locBuyerId: card.locBuyerId || '',
      locName:
        card.locType === 'SHOP' ? businessName || '' : card.locName || '',
    };
  });

  const mappedPaginationInfo = {
    pageNumber: Number(paginationInfo.pn) || 0,
    pageSize: Number(paginationInfo.ps) || 0,
    totalCreditCards: filterConfig?.filterType
      ? oldPaginationInfo?.totalCreditCards || 0
      : paginationInfo.totalCC || 0,
    totalSearchedCreditCards: filterConfig?.filterType
      ? paginationInfo.totalCC || 0
      : 0,
    totalPayPalAccounts: paginationInfo.totalPBA || 0,
    totalGiftCards: paginationInfo.totalGC || 0,
    totalProXtraDollars: paginationInfo.totalPGC || 0,
    totalCheckingAccounts: paginationInfo.totalCA || 0,
    totalKeyTags: paginationInfo.totalKT || 0,
    totalLinesOfCredit: paginationInfo.totalLOC || 0,
    totalPages: Number(paginationInfo.totalPages) || 0,
    paymentsOnCurrentPage: paymentCards.length || 0,
    citiCardsSummary: paginationInfo.citiCardsSummary || [],
    t2cNumber: paginationInfo.t2cNumber || undefined,
  };

  return {
    paymentCards: {
      paymentCards: mappedCards,
      response: paymentCards,
    },
    paginationInfo: mappedPaginationInfo,
    t2cApprovers,
  };
};

export const mapB2CPaymentCardsToCommonModel = (
  cardType: CardType,
  paymentsCards: Partial<IB2CPaymentCard>[],
  paginationInfo: Partial<IB2CPaginationInfo>,
): IPaymentCardsCommonData => {
  const isPaypalCard = cardType === CardType.PAYPAL_ACCOUNT;
  const filteredPaymentsCards = paymentsCards.filter((card) =>
    isPaypalCard ? !!card.paypalAccountId : !card.paypalAccountId,
  );

  const mappedFilteredPaymentsCard = filteredPaymentsCards.map(
    (card): IPaymentCardCommonData => {
      const billingAddress = card.billingAddress as IB2CBillingAddress;
      const postalDetails = billingAddress?.postalDetails;

      return {
        addressId: billingAddress?.addrIdentifier || '',
        addressLine1: postalDetails?.addressLine1 || '',
        addressLine2: postalDetails?.addressLine2,
        cardBrand: card.cardBrand || '',
        cardExpiryMonth: card.cardExpiryMonth || '',
        cardExpiryYear: card.cardExpiryYear || '',
        cardHolderName: card.cardHolderName || '',
        cardNickName: card.cardNickName,
        cardNumber: card.cardNumber || '',
        cardNumberLast4: card.cardNumberLast4 || '',
        cardStatus: undefined,
        cardType: card.cardType || '',
        city: postalDetails?.city || '',
        authorizeFlag: undefined,
        isCardExpired: card.isCardExpired,
        isDefault: card.isDefault || false,
        lastModifiedDate: card.lastModifiedDate,
        paymentId: card.paymentId || '',
        state: postalDetails?.state || '',
        zipCode: postalDetails?.zipCode || '',
        buyerId: card.buyerId || '',
        paypalAccountId: card.paypalAccountId,
        primaryPhone: { number: billingAddress?.primaryPhone?.number || '' },
        instantCheckoutDefault: card.instantCheckoutDefault,
      };
    },
  );

  const mappedPaymentCardsLength = mappedFilteredPaymentsCard.length;
  const paymentCardsLength = Number(paginationInfo?.totalNumberOfRecords);
  const restCardsLength = paymentCardsLength - mappedPaymentCardsLength;

  const mappedPaginationInfo = {
    pageNumber: Number(paginationInfo.pageNumber) || 0,
    pageSize: Number(paginationInfo.pageSize) || 0,
    totalCreditCards:
      (cardType === CardType.CREDIT_CARD
        ? mappedPaymentCardsLength
        : restCardsLength) || 0,
    totalPayPalAccounts:
      (cardType === CardType.PAYPAL_ACCOUNT
        ? mappedPaymentCardsLength
        : restCardsLength) || 0,
    totalGiftCards: 0,
    totalSearchedCreditCards: 0,
    totalProXtraDollars: 0,
    totalCheckingAccounts: 0,
    totalKeyTags: 0,
    totalLinesOfCredit: 0,
    totalPages: Number(paginationInfo.totalPages) || 0,
    paymentsOnCurrentPage: filteredPaymentsCards.length,
  };

  return {
    paymentCards: {
      paymentCards: mappedFilteredPaymentsCard,
      response: paymentsCards,
    },
    paginationInfo: mappedPaginationInfo,
    t2cApprovers: null,
  };
};

export const mapB2BEditCreditCardToRequestModel = (
  values: TPaymentFormValues,
  billingAddress: IB2BBillingAddress | null,
  fullCreditCardData: IB2BPaymentCard,
): IB2BPaymentCard => {
  const {
    isDefault,
    cardNickName,
    cardHolderName,
    cardExpiryMonth,
    cardExpiryYear,
    addressLine1,
    addressLine2,
    zipCode,
    cityState,
  } = values;
  const [cityName, stateCode] = cityState.split(', ');
  const addressId = billingAddress?.addressId;
  const { agreementCode, ein } = fullCreditCardData;

  const requestModel = {
    ...fullCreditCardData,
    billingAddress: {
      addressId: addressId || null,
      postalCode: zipCode,
      addressLine1,
      addressLine2: addressLine2 || '',
      cityName,
      stateCode,
      countryCode: billingAddress ? billingAddress.countryCode : 'US',
      name: billingAddress ? billingAddress.name : null,
      isDefault: billingAddress ? billingAddress.isDefault : null,
    },
    isDefault,
    cardNickName: cardNickName || NULLIFY_FIELD,
    cardHolderName: cardHolderName || NULLIFY_FIELD,
  };

  if (cardExpiryMonth && cardExpiryYear) {
    requestModel.cardExpiryMonth = cardExpiryMonth;
    requestModel.cardExpiryYear = cardExpiryYear;
  }

  if (agreementCode) {
    // To avoid API failures if agreementCode/eui field is empty or undefined
    requestModel.agreementCode = agreementCode || ' ';
    requestModel.ein = ein || ' ';
  }

  return requestModel;
};

export const mapB2CEditCreditCardToRequestModel = (
  values: TPaymentFormValues,
  fullCreditCardData: IB2CPaymentCard,
): TB2CEditPaymentRequestData => {
  const {
    isDefault,
    cardNickName,
    cardHolderName,
    cardExpiryMonth,
    cardExpiryYear,
    billingAddressID,
  } = values;

  const requestModel = {
    ...fullCreditCardData,
    billingAddress: {
      addrIdentifier: billingAddressID,
    },
    isDefault,
    cardNickName,
    cardHolderName,
  };

  if (cardExpiryMonth && cardExpiryYear) {
    requestModel.cardExpiryMonth = cardExpiryMonth;
    requestModel.cardExpiryYear = cardExpiryYear;
  }

  return requestModel;
};
